import { call, put, all, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { tokenBalanceUrl, tokenCountdownsUrl, tokenPackagesUrl } from '../../configurations/api/url';
import { httpRequest } from '../types';
import { globalErrorHandler } from '../error/saga';
import { SentryCapture } from '../../analytics/Sentry';
import {
    getTokenBalanceSuccess,
    getTokenBalanceFail,
    getTokenPackagesSuccess,
    getTokenPackagesFail,
    purchaseTokensSuccess,
    purchaseTokensFail,
    consumeTokensSuccess,
    consumeTokensFail,
    ITokenCountdownPayload,
    ITokenCountdownResponse,
    getTokenCountdownSuccess,
    getTokenCountdownFail,
} from './actions';
import { GET_TOKEN_BALANCE, GET_TOKEN_PACKAGES, PURCHASE_TOKENS, CONSUME_TOKENS, GET_TOKEN_COUNTDOWN } from './types';
import { showToastMessage } from '../../utils/AppUtils';

function* getTokenCountdown({
    payload,
    resolve,
    reject,
}: {
    payload: ITokenCountdownPayload;
    resolve: (data: ITokenCountdownResponse) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, `${tokenCountdownsUrl}/${payload.contestId}`, httpRequest.GET, {}, 0, 0);
        const { data } = response;
        if (resolve) {
            resolve(data);
        }

        yield put(getTokenCountdownSuccess(data));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getTokenCountdownFail(error));
        if (reject) {
            reject(error);
        }
    }
}

function* getTokenBalanceSaga({
    resolve,
    reject,
}: {
    resolve: (data: any) => Promise<void>;
    reject: (reason: any) => Promise<void>;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, tokenBalanceUrl, httpRequest.GET, {}, 0, 0);
        const { data } = response;

        yield put(
            getTokenBalanceSuccess({
                balance: data.token.balance,
                token: data.token,
            }),
        );
        if (resolve) resolve(data);
    } catch (error: any) {
        if (reject) reject(error);
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(getTokenBalanceFail({ error: error.message || 'Failed to fetch token balance' }));
    }
}

function* getTokenPackagesSaga(): Generator<any, void, any> {
    try {
        const response = yield call(api, tokenPackagesUrl, httpRequest.GET, {}, 0, 0);
        const { data } = response.data;

        yield put(getTokenPackagesSuccess({ packages: data }));
    } catch (error: any) {
        console.error(error);
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(getTokenPackagesFail({ error: error.message || 'Failed to fetch token packages' }));
    }
}

function* purchaseTokensSaga({
    payload,
    resolve,
    reject,
}: {
    payload: {
        packageId: string;
        tokens: number;
        price: number;
        resolve: (value: any) => void;
        reject: (reason?: any) => void;
    };
    resolve: any;
    reject: any;
}): Generator<any, void, any> {
    try {
        // For now, we'll just simulate a successful purchase
        // The actual payment processing will be handled by the payment flow
        yield call(showToastMessage, 'Token purchase initiated', 'success');

        if (resolve) {
            resolve({ success: true, packageId: payload.packageId });
        }

        yield put(purchaseTokensSuccess({ packageId: payload.packageId, result: { success: true } }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(purchaseTokensFail({ error: error.message || 'Failed to purchase tokens' }));

        if (reject) {
            reject(error);
        }
    }
}

function* consumeTokensSaga({
    payload,
    resolve,
    reject,
}: {
    payload: {
        submissionId: string;
        contestId: string;
        tokens: number;
        resolve: (value: any) => void;
        reject: (reason?: any) => void;
    };
    resolve: any;
    reject: any;
}): Generator<any, void, any> {
    try {
        // For now, we'll just simulate a successful vote
        // The actual API call will be implemented later
        yield call(showToastMessage, 'Vote cast successfully!', 'success');

        if (resolve) {
            resolve({ success: true, submissionId: payload.submissionId });
        }

        yield put(consumeTokensSuccess({ submissionId: payload.submissionId, result: { success: true } }));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        yield put(consumeTokensFail({ error: error.message || 'Failed to cast vote' }));

        if (reject) {
            reject(error);
        }
    }
}

export default function* tokenSaga(): Generator<any, any, any> {
    yield all([
        takeLatest<any>(GET_TOKEN_BALANCE, getTokenBalanceSaga),
        takeLatest(GET_TOKEN_PACKAGES, getTokenPackagesSaga),
        takeLatest<any>(PURCHASE_TOKENS, purchaseTokensSaga),
        takeLatest<any>(CONSUME_TOKENS, consumeTokensSaga),
        takeLatest<any>(GET_TOKEN_COUNTDOWN, getTokenCountdown),
    ]);
}
